import { isNotEmpty } from 'rambda';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router';
import { ApplicationListItem } from '@/components/contacts/application-list/application-list-item';
import FriendApplicationListItem from '@/components/contacts/application-list/friend-application-list-item';
import EmptyLayout from '@/components/layouts/empty-layout';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import useFriendRequestCount from '@/hooks/contacts/use-friend-request-count';
import { useIsDesktop } from '@/hooks/use-user-platform';
import useFriendApplications from '@/hooks/users/use-friend-applications';
import type { PromiseLoaderReturnType } from '@/routes';

export default function FriendApplicationsRoute() {
  const loaderData = useLoaderData<PromiseLoaderReturnType>();
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const { data } = useFriendApplications();
  const { markAllAsRead } = useFriendRequestCount();

  useEffect(() => {
    markAllAsRead();
  }, []);

  return (
    <PageLayout
      title={t('contact.applications.title')}
      headerConfig={{
        navigateBack: !isDesktop,
      }}>
      <div className='mx-5'>
        <h2 className='font-bold pb-4 text-body1'>{t('contact.applications.pending-applications')}</h2>

        <LoadingLayout promise={loaderData?.promises}>
          {() =>
            isNotEmpty(data?.applications) ? (
              <ul className='flex w-full flex-col items-stretch gap-6 desktop:overflow-y-auto'>
                {data?.applications.map((application) => (
                  <li
                    key={application.id}
                    id={`pending-application-${application.id}`}>
                    <FriendApplicationListItem application={application} />
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyLayout
                className='flex h-20'
                label='contact.applications.empty'
              />
            )
          }
        </LoadingLayout>

        <h2 className='font-bold pb-4 pt-6 text-body1'>{t('contact.applications.past-applications')}</h2>

        <ul className='flex w-full flex-col items-stretch gap-6 desktop:overflow-y-auto desktop:h-[8.125rem]'>
          <LoadingLayout promise={loaderData?.promises}>
            {() =>
              data?.pastApplications.map((application) => (
                <li
                  key={application.id}
                  id={`past-application-${application.id}`}>
                  <ApplicationListItem
                    application={application}
                    response={application.status}
                  />
                </li>
              ))
            }
          </LoadingLayout>
        </ul>
      </div>
    </PageLayout>
  );
}
